/**
 * The presence slice of the wire — Task PW1 (pillar web), consuming `GET /presence`
 * and `GET /presence/summary`, both already built and tested against `openapi.yaml`'s
 * `Presence` tag (Task P2). Hand-written to mirror the contract, exactly as `geo.ts`'s
 * own header describes for its slice: transcribe what the screen reads, not the whole
 * document, and this task does not touch `openapi.yaml`.
 *
 * A field the contract marks `nullable` is `T | null` here — the server sends the key
 * with a `null` value, it never omits it — and `exactOptionalPropertyTypes` is on, so
 * an absent value and an explicit `null` are different facts (`PRODUCT.md` §4).
 *
 * ── Not a productivity score ──────────────────────────────────────────────────
 *
 * `openapi.yaml`'s own description of `listPresence`: "no field here is a number
 * computed FROM these facts — that is the scoring module's separate, explainable,
 * confidence-bounded job." Nothing below is a derived figure; every member is a
 * status word, a boolean, an id, or a timestamp the server measured directly.
 */

import { qs, type QueryValue } from './paths';

/** `PresenceStatus` of `openapi.yaml` — computed on every read, never stored. */
export const PRESENCE_STATUSES = [
  'working_onsite',
  'working_remote',
  'on_break',
  'offline',
] as const;

export type PresenceStatus = (typeof PRESENCE_STATUSES)[number];

/**
 * `PresenceWhere.trust` — migration 047's geo-check-in verdict, surfaced here as a
 * neutral flag for a manager to review. Never an accusation and never fed into a
 * score (`openapi.yaml`'s own words on the `PresenceWhere` schema).
 */
export const PRESENCE_TRUST_VALUES = ['trusted', 'suspect', 'unverified'] as const;

export type PresenceTrust = (typeof PRESENCE_TRUST_VALUES)[number];

/** The `office` member of `PresenceWhere` — `null` when no geofence matched. */
export interface PresenceOffice {
  readonly id: string;
  readonly name: string;
}

/**
 * `PresenceWhere` of `openapi.yaml` — TODAY's latest geo check-in. `null` at the top
 * level when the employee has not checked in today (WP29: never a check-in from a
 * prior day carried forward).
 */
export interface PresenceWhere {
  readonly office: PresenceOffice | null;
  /** `null` (migration 055) when this employee has exercised the PDPL's location
   * opt-out: they checked in, no location was collected, so there is no geofence
   * answer. Never `false` for that case — `false` means "outside the office". */
  readonly within_geofence: boolean | null;
  readonly trust: PresenceTrust;
  readonly checked_in_at: string;
}

/**
 * `PresenceOnWhat` of `openapi.yaml` — the employee's open task timer. `null` at the
 * top level when no timer is open. `task_title` is `null` when the title was not
 * snapshotted (or the snapshot itself was blank); `achau_task_id` is always present.
 */
export interface PresenceOnWhat {
  readonly achau_task_id: string;
  readonly task_title: string | null;
}

/**
 * `PresenceRow` of `openapi.yaml`. `required: [employee_id, full_name, department,
 * status, where, on_what, last_seen_at]` — every member here is present, `T | null`
 * where the schema allows it.
 */
export interface PresenceRow {
  readonly employee_id: string;
  readonly full_name: string;
  readonly department: string | null;
  readonly status: PresenceStatus;
  readonly where: PresenceWhere | null;
  readonly on_what: PresenceOnWhat | null;
  readonly last_seen_at: string | null;
}

/** The level 02 §11 resolved the caller's field of view to, on `PresenceListResponse`. */
export type PresenceScopeLevel = 'self' | 'department' | 'company';

/** `PresenceListResponse` — `{ data, scope }`. `x-wp-pagination: none`: every row in scope, one response. */
export interface PresenceListResponse {
  readonly data: readonly PresenceRow[];
  readonly scope: { readonly level: PresenceScopeLevel };
}

/**
 * `PresenceSummary` of `openapi.yaml` — counts per status for the caller's resolved
 * scope. `total` is derived server-side (`working_onsite + working_remote + on_break +
 * offline`) and always equals the row count `GET /presence` would return for the same
 * caller and filter; it is read here, never recomputed client-side.
 */
export interface PresenceSummary {
  readonly working_onsite: number;
  readonly working_remote: number;
  readonly on_break: number;
  readonly offline: number;
  readonly total: number;
}

export const PRESENCE_PATH = '/api/v1/presence';
export const PRESENCE_SUMMARY_PATH = '/api/v1/presence/summary';

/** The filters both `GET /presence` and `GET /presence/summary` accept. */
export interface PresenceQuery {
  readonly status?: PresenceStatus | undefined;
  readonly officeLocationId?: string | undefined;
  readonly departmentId?: string | undefined;
}

/**
 * `GET /presence?status=&office_location_id=&department_id=`.
 *
 * All three are **server** filters, intersected with the caller's resolved scope —
 * never applied client-side over a page already in hand (`paths.ts`'s own rule for
 * every narrowing in this file). `x-wp-pagination: none`, so there is no `limit` to
 * choose: every row in scope arrives in the one response.
 */
export function presencePath(query: PresenceQuery = {}): string {
  const params: Readonly<Record<string, QueryValue>> = {
    status: query.status,
    office_location_id: query.officeLocationId,
    department_id: query.departmentId,
  };
  return `${PRESENCE_PATH}${qs(params)}`;
}

/**
 * `GET /presence/summary?department_id=`.
 *
 * `getPresenceSummary` accepts ONLY `department_id` (openapi.yaml / routes.ts) — not
 * `status` and not `office_location_id`. Sending either is silently dropped server-side,
 * which is worse than not sending it: the tiles would then silently describe a wider
 * population than an office-filtered list. So the office filter narrows the LIST only;
 * the tiles reflect the status/department scope, and `presence.md` says so.
 */
export function presenceSummaryPath(
  query: Omit<PresenceQuery, 'status' | 'officeLocationId'> = {},
): string {
  const params: Readonly<Record<string, QueryValue>> = {
    department_id: query.departmentId,
  };
  return `${PRESENCE_SUMMARY_PATH}${qs(params)}`;
}
